import { askConfirm } from '../lib/askConfirm';
import { HelpBtn } from './HelpBtn';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { X, Play, Pause, Square, Save, Trash2 } from 'lucide-react';
import MaskToggle from './MaskToggle';
import { LiveProfile, OffRoute } from './TrackLive';
import { TRACK_PROFILES, trackStats, fmtDur } from '../lib/track';
import { unlockAudio } from '../lib/sound';
import { useTrackRec, curPos, startTrack, pauseTrack, resumeTrack, stopTrack, clearTrack, addWaypoint, staleDone } from '../lib/trackRec';
import { modeTracks, TRK_FIELDS, poisFor, trackWork, windOver, WIND_MAX, MODE_TRACKS, sportStats } from '../lib/trackModes';
import SosButton from './SosButton';
import { useWeatherWatch } from '../lib/weatherWatch';
import { precAsk } from '../lib/precision';
import { useAgents, waitState } from '../lib/agents';
import { modeId, ml } from '../lib/profiles';
import { SCREEN_MODES, setScreenMode, useScreenMode } from '../lib/screenMode';
import { RtkBadge } from './PrecisionDialog';
import { beep } from '../lib/sound';
import { uid } from '../lib/model';

const n1 = (v, d = 1) => (+v).toFixed(d).replace('.', ',');

export const TrackStats = ({ t, s, testid, profile }) => {
  const sp = sportStats(profile, s);
  return (
  <div className="trk-stats" data-testid={testid}>
    <span><small>{t.trkLen}</small><b data-testid={`${testid}-km`}>{n1(s.km, 2)} km</b></span>
    <span><small>{t.trkTime}</small><b>{fmtDur(s.ms)}</b></span>
    <span><small>Ø</small><b data-testid={`${testid}-speed`}>{n1(s.kmh)} km/h</b></span>
    <span><small>{t.trkUpDown}</small><b>↑ {Math.round(s.up)} m · ↓ {Math.round(s.down)} m</b></span>
    {sp.pace && <span><small>{t.stPace}</small><b data-testid={`${testid}-pace`}>{sp.pace}</b></span>}
    {sp.kcal != null && modeId() !== 'garden' && <span title={t.stKcalHint}><small>{t.stKcal}</small><b data-testid={`${testid}-kcal`}>{sp.kcal}</b></span>}
  </div>
  );
};

function AgentPick({ t, k, meta, setMeta }) {
  const kind = k === 'agFert' ? 'fert' : 'spray', list = useAgents().filter((a) => a.kind === kind);
  const pick = (id) => { const a = list.find((x) => x.id === id); if (!a) return setMeta({ ...meta, agentId: '' }); setMeta({ ...meta, agentId: a.id, agent: a.name, reg: a.reg, ...(kind === 'fert' ? { fertRate: a.rate } : { rate: a.rate, wait: a.wait }) }); };
  return <label>{t.tlf_agentPick}<select data-testid="track-field-agentPick" value={meta.agentId || ''} onChange={(e) => pick(e.target.value)}><option value="">{list.length ? '–' : t.agEmptyShort}</option>{list.map((a) => <option key={a.id} value={a.id}>{a.name}{a.reg ? ` · ${a.reg}` : ''}</option>)}</select></label>;
}

function Field({ t, f, v, set, plots }) {
  const d = TRK_FIELDS[f], lbl = `${t['tlf_' + f]}${d.unit ? ` (${d.unit})` : ''}`;
  if (d.type === 'plot') return <label>{lbl}<select data-testid="track-field-plot" value={v || ''} onChange={(e) => set(e.target.value)}><option value="">–</option>{plots.map((r) => <option key={r.id} value={r.id}>{r.title}</option>)}</select></label>;
  if (d.type === 'sel') return <label>{lbl}<select data-testid={`track-field-${f}`} value={v || ''} onChange={(e) => set(e.target.value)}><option value="">–</option>{d.opts.map((o) => <option key={o} value={o}>{t[`tlo_${f}_${o}`] || o}</option>)}</select></label>;
  return <label>{lbl}<input data-testid={`track-field-${f}`} inputMode={d.type === 'num' ? 'decimal' : 'text'} value={v || ''} onChange={(e) => set(e.target.value)} /></label>;
}

// input mask of the chosen profile (activity, vehicle, agent, auto-step, speed limit …)
// screen behaviour during a tour (leisure)
function ScreenPick({ t, compact }) {
  const m = useScreenMode();
  return (
    <div className={`scr-pick ${compact ? 'compact' : ''}`} data-testid="screen-mode-pick">
      <b>{t.scrTitle}</b>
      <div className="scr-opts">{SCREEN_MODES.map((k) => <button key={k} type="button" className={m === k ? 'on' : ''} data-testid={`screen-mode-${k}`} aria-pressed={m === k} onClick={() => setScreenMode(k)}>{t['scr_' + k]}</button>)}</div>
      {!compact && <small>{t['scrD_' + m]}</small>}
    </div>
  );
}

function TrackMask({ t, k, meta, setMeta, onStart, plots }) {
  const P = TRACK_PROFILES[k];
  const box = useRef(null);
  useEffect(() => { box.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); }, [k]);
  return (
    <div className="trk-mask" ref={box} data-testid="track-mask" style={{ '--c': P.color }}>
      <b>{P.icon} {t['trkP_' + k]}</b>
      <div className="trk-mask-grid">{P.fields.map((f) => (f === 'agentPick' ? <AgentPick key={f} t={t} k={k} meta={meta} setMeta={setMeta} /> : <Field key={f} t={t} f={f} v={meta[f]} plots={plots} set={(v) => setMeta({ ...meta, [f]: v })} />))}</div>
      <button className="btn btn-green" data-testid="track-start-btn" onClick={onStart}><Play size={16} /> {t.trkStart}</button>
    </div>
  );
}

// live speed (with limit warning), GNSS fix, worked area + quick POIs (outdoor)
function LiveBar({ t, rec }) {
  const P = TRACK_PROFILES[rec.profile], m = rec.meta || {}, w = trackWork(rec);
  const max = parseFloat(String(m.maxKmh ?? '').replace(',', '.')) || P.maxKmh;
  const poi = (k) => { const c = curPos(); if (!c) return toast.error(t.wpNoGps); addWaypoint({ id: uid('wp'), kind: 'note', poi: k, status: null, note: t['poi_' + k], lat: c.lat, lon: c.lon, alt: c.alt ?? null, acc: c.acc ?? null, t: Date.now(), photos: 0, videos: 0, memos: 0 }); toast.success(t['poi_' + k]); };
  return (
    <div className="trk-live">
      <div className={`trk-speed ${rec.ov ? 'ov' : ''}`} data-testid="track-speed"><b>{rec.v != null && rec.status === 'rec' ? n1(rec.v) : '–'}</b><small>km/h{max ? ` · max ${max}` : ''}</small></div>
      {rec.ov && <p className="pr-alert ut-gas-alarm" data-testid="track-speed-warn">⚠️ {t.tlOverWarn.replace('{v}', max)}</p>}
      <div className="row trk-live-row"><RtkBadge t={t} testid="track-rtk-badge" />{rec.over > 0 && <small data-testid="track-over-count">⚠️ {rec.over}× {t.tlOver}</small>}{w.area != null && <small data-testid="track-area">{Math.round(w.area)} m² · {w.ha.toFixed(2)} ha{w.use != null ? ` · ${w.use.toFixed(1)} ${w.useUnit}` : ''}</small>}</div>
      {rec.profile in MODE_TRACKS.outdoor && (
        <div className="row trk-poi">{poisFor(rec.profile).map((k) => <button key={k} className="btn btn-outline btn-sm" data-testid={`track-poi-${k}`} onClick={() => poi(k)}>{t['poi_' + k]}</button>)}{rec.cur?.alt != null && <small data-testid="track-alt">⛰️ {Math.round(rec.cur.alt)} m {t.aslShort}</small>}</div>
      )}
    </div>
  );
}

// bottom sheet: choose profile -> live recording -> name & save
export default function TrackSheet({ t, onSave, routeNo, onDiscard, refs = [], plots = [], tracks = [] }) {
  const rec = useTrackRec();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [refId, setRefId] = useState('');
  const [selP, setSelP] = useState(null);
  const [meta, setMeta] = useState({});
  useEffect(() => { if (rec?.ov) beep(880, 660); }, [rec?.ov, rec?.over]); // eslint-disable-line react-hooks/exhaustive-deps
  const [, tick] = useState(0);
  useEffect(() => { const on = (e) => { setOpen(true); if (e.detail?.ref) setRefId(e.detail.ref); if (e.detail?.profile) setSelP(e.detail.profile); }; window.addEventListener('giss-track-open', on); return () => window.removeEventListener('giss-track-open', on); }, []);
  useEffect(() => { if (staleDone && rec?.status === 'done') { setOpen(true); toast.message(t.trkStaleDone.replace('{d}', new Date(rec.start).toLocaleDateString(t.locale)), { duration: 9000 }); } }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const wx = useWeatherWatch(t, rec?.status === 'rec');
  useEffect(() => { if (rec?.status !== 'rec') return undefined; const id = setInterval(() => tick((x) => x + 1), 1000); return () => clearInterval(id); }, [rec?.status]);
  useEffect(() => { if (rec?.status === 'done') setName(`${t['trkP_' + rec.profile]} ${new Date(rec.start).toLocaleDateString()}`); }, [rec?.status]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!rec && !open) return null;
  const P = rec && TRACK_PROFILES[rec.profile];
  const s = rec && trackStats({ ...rec, end: rec.end || (rec.status === 'pause' ? rec.pauseAt : Date.now()) });
  const save = () => {
    if (rec.pts.length < 2) return toast.error(t.trkTooShort);
    onSave({ id: uid('trk'), name: name.trim() || t['trkP_' + rec.profile], profile: rec.profile, meta: rec.meta || null, over: rec.over || 0, no: rec.no || routeNo, wps: rec.wps || [], pts: rec.pts, start: rec.start, end: rec.end, paused: rec.paused });
    clearTrack(); setOpen(false); toast.success(t.trkSaved);
  };
  return (
    <div className={`trk-sheet mask ${!rec && modeId() === 'outdoor' ? 'trk-pick' : ''}`} data-testid="track-sheet" style={P ? { '--c': P.color } : undefined}>
      <div className="trk-head mask-keep">
        <b>🧭 {ml(t, 'trkTitle')}</b>
        {rec && <span className={`trk-st st-${rec.status}`} data-testid="track-status">{P.icon} {t['trkS_' + rec.status]}</span>}
        <SosButton t={t} />
        <MaskToggle t={t} id="track" />
        <HelpBtn k="n:meas_profile" testid="track-help" title={t.helpHere} />{!rec && <button className="icon-btn" data-testid="track-close-btn" onClick={() => setOpen(false)}><X size={16} /></button>}
      </div>
      {!rec && (
        <>
          <p className="trk-hint" data-testid="track-hint">{Object.keys(modeTracks(modeId())).length ? t.trkChoose : t.trkNone}</p>
          <div className="trk-profiles">
            {Object.entries(modeTracks(modeId())).map(([k, p]) => (
              <button key={k} className={`trk-prof ${selP === k ? 'on' : ''}`} style={{ '--c': p.color }} data-testid={`track-profile-${k}`} onClick={() => { setSelP(k); setMeta({ ...(p.maxKmh ? { maxKmh: String(p.maxKmh) } : {}), ...(p.fields.includes('windMax') ? { windMax: String(WIND_MAX) } : {}) }); }}>
                <span>{p.icon}</span><b>{t['trkP_' + k]}</b><small>{t['trkD_' + k]}</small>
              </button>
            ))}
          </div>
          {selP && <TrackMask t={t} k={selP} meta={meta} setMeta={setMeta} plots={plots} onStart={async () => { unlockAudio(); const wo = windOver(meta); if (wo && (await precAsk('wind', { v: meta.wind, max: meta.windMax || WIND_MAX })) !== 'ok') return; const ws = selP === 'agHarvest' && meta.plot ? waitState(tracks, meta.plot) : null, wh = ws && ws.st !== 'green'; if (wh && (await precAsk('waitH', { d: new Date(ws.end).toLocaleDateString(t.locale), a: ws.agent })) !== 'ok') return; startTrack(selP, routeNo, refId || null, { ...meta, ...(wo ? { windOver: true } : {}), ...(wh ? { waitOver: true } : {}) }); setSelP(null); }} />}
          <label className="trk-ref">{t.devRef}
            <select data-testid="track-ref-select" value={refId} onChange={(e) => setRefId(e.target.value)}>
              <option value="">{t.devNone}</option>
              {refs.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </label>
          {modeId() === 'outdoor' ? <ScreenPick t={t} /> : <p className="trk-sub">{t.trkScreenHint}</p>}
        </>
      )}
      {rec && rec.status !== 'done' && (
        <>
          <LiveBar t={t} rec={rec} />
          {modeId() === 'outdoor' && <ScreenPick t={t} compact />}
          {wx && <div className={`trk-wx ${wx.kind}`} data-testid="track-weather-status">{wx.kind === 'ok' ? t.wxOk : t['wxWarn_' + wx.kind].replace('{at}', wx.at ? `${t.wxAt} ${wx.at}` : t.wxNow).replace('{v}', wx.v ?? '')}</div>}
          <TrackStats t={t} s={s} profile={rec.profile} testid="track-live-stats" />
          {rec.ref && !String(rec.ref).startsWith('plan_') && <OffRoute t={t} pos={curPos()} lines={refs.find((r) => r.id === rec.ref)?.lines} />}
          <LiveProfile pts={rec.pts} />
          <p className="trk-sub" data-testid="track-points">R{rec.no || routeNo} · {rec.pts.length} {t.trkPoints} · 📍 <b data-testid="track-wp-count">{(rec.wps || []).length}</b>{rec.acc ? ` · GPS ±${Math.round(rec.acc)} m` : ''} · {t.trkStartAt} {new Date(rec.start).toLocaleTimeString()}</p>
          <div className="row">
            {rec.status === 'rec'
              ? <button className="btn btn-outline" data-testid="track-pause-btn" onClick={pauseTrack}><Pause size={16} /> {t.trkPause}</button>
              : <button className="btn btn-green" data-testid="track-resume-btn" onClick={() => { unlockAudio(); resumeTrack(); }}><Play size={16} /> {t.trkResume}</button>}
            <button className="btn btn-red" data-testid="track-stop-btn" onClick={stopTrack}><Square size={16} /> {t.trkStop}</button>
          </div>
        </>
      )}
      {rec?.status === 'done' && (
        <>
          <TrackStats t={t} s={s} profile={rec.profile} testid="track-final-stats" />
          <div className="row">
            <input className="trk-name" data-testid="track-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder={t.trkName} />
            <button className="btn btn-green" data-testid="track-save-btn" onClick={save}><Save size={16} /> {t.trkSave}</button>
            <button className="btn btn-outline" data-testid="track-discard-btn" onClick={async () => (await askConfirm(t.trkDiscardQ)) && (onDiscard?.((rec.wps || []).map((w) => w.id)), clearTrack(), setOpen(false))}><Trash2 size={16} /> {t.trkDiscard}</button>
          </div>
        </>
      )}
    </div>
  );
}
